import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const AboutPage = () => {
  const [directors, setDirectors] = useState({ sections: [], availableSections: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDirectors = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/directors');
        const data = await response.json();

        if (data.message === 'Data fetched successfully!') {
          setDirectors({
            sections: data.sections,
            availableSections: data.availableSections
          });
        } else {
          toast.error('Failed to fetch directors data');
        }
      } catch (error) {
        console.error('Error fetching directors:', error);
        toast.error('Failed to fetch directors data');
      } finally {
        setLoading(false);
      }
    };

    fetchDirectors();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#1A8D32] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">About Us</h1>
          <p className="text-xl">Innovative Eco Krishi - Pioneering Sustainable Agriculture</p>
        </div>
      </div>

      {/* Company Information */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Our History */}
          <div>
            <h2 className="text-2xl font-bold mb-4 text-[#1E4C9A]">Our History</h2>
            <p className="text-gray-600 mb-4">
              Founded with a vision to revolutionize agriculture in Nepal, Innovative Eco Krishi has been at the forefront of sustainable farming practices since its inception. Our journey began with a simple mission: to make agriculture more efficient, sustainable, and profitable for Nepalese farmers.
            </p>
            <p className="text-gray-600">
              Over the years, we have grown from a small initiative to a leading agricultural company, implementing innovative technologies and practices that have transformed the way farming is done in Nepal.
            </p>
          </div>

          {/* Our Mission */}
          <div>
            <h2 className="text-2xl font-bold mb-4 text-[#1E4C9A]">Our Mission</h2>
            <p className="text-gray-600 mb-4">
              Our mission is to promote sustainable agricultural practices that enhance productivity while preserving the environment. We are committed to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Implementing eco-friendly farming techniques</li>
              <li>Supporting local farmers with modern technology</li>
              <li>Promoting organic and sustainable agriculture</li>
              <li>Ensuring food security through innovative practices</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Directors Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1A8D32]"></div>
            </div>
          ) : (
            directors.sections
              .filter(section => section.directors.length > 0)
              .map((section, sectionIndex) => (
                <div key={section.section_id} className="mb-16">
                  <h2 className="text-3xl font-bold text-center mb-8 text-[#1E4C9A]">{section.section_name}</h2>
                  
                  {/* First Section - Special Layout for First Director */}
                  {sectionIndex === 0 && section.directors.length > 0 && (
                    <div className="flex justify-center mb-12">
                      <div className="w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden border-2 border-[#1A8D32]">
                        <div className="aspect-[4/3] relative">
                          <img
                            src={section.directors[0].image_url}
                            alt={section.directors[0].alt_text || section.directors[0].name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Image failed to load:', section.directors[0].image_url);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="p-8 text-center">
                          <h3 className="text-2xl font-bold mb-3 text-[#1E4C9A]">{section.directors[0].name}</h3>
                          <p className="text-[#1A8D32] text-lg font-medium mb-2">{section.directors[0].position}</p>
                          <p className="text-gray-600">{section.directors[0].department}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Directors Grid - 3 per row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {section.directors.slice(sectionIndex === 0 ? 1 : 0).map((director) => (
                      <div key={director.director_id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-[#1A8D32]">
                        <div className="aspect-[4/3] relative">
                          <img
                            src={director.image_url}
                            alt={director.alt_text || director.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Image failed to load:', director.image_url);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="p-6 text-center">
                          <h3 className="text-xl font-semibold mb-2 text-[#1E4C9A]">{director.name}</h3>
                          <p className="text-[#1A8D32] font-medium mb-1">{director.position}</p>
                          <p className="text-gray-600">{director.department}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Values Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-8 text-[#1E4C9A]">Our Core Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-lg border border-[#1A8D32]">
            <h3 className="text-xl font-semibold mb-4 text-[#1E4C9A]">Sustainability</h3>
            <p className="text-gray-600">
              We are committed to practices that ensure long-term environmental health and agricultural productivity.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-[#1A8D32]">
            <h3 className="text-xl font-semibold mb-4 text-[#1E4C9A]">Innovation</h3>
            <p className="text-gray-600">
              We constantly seek new and better ways to improve agricultural practices and outcomes.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-[#1A8D32]">
            <h3 className="text-xl font-semibold mb-4 text-[#1E4C9A]">Community</h3>
            <p className="text-gray-600">
              We believe in working together with farmers and communities to create sustainable solutions.
            </p>
          </div>
        </div>
      </div>

      {/* Agricultural Focus Section */}
      <div className="bg-[#1A8D32] bg-opacity-10 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1E4C9A]">Our Agricultural Focus</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border border-[#1A8D32]">
              <h3 className="text-xl font-semibold mb-4 text-[#1E4C9A]">Sustainable Farming</h3>
              <p className="text-gray-600">
                We promote eco-friendly farming techniques that maintain soil health and biodiversity while maximizing productivity.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg border border-[#1A8D32]">
              <h3 className="text-xl font-semibold mb-4 text-[#1E4C9A]">Technology Integration</h3>
              <p className="text-gray-600">
                We integrate modern technology with traditional farming knowledge to create efficient and sustainable agricultural solutions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage; 